import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const Wrapper = styled.div`padding: 22px; color: #e2e8f0;`;
const Header = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 18px;`;
const Title = styled.h2`margin: 0; display: flex; align-items: center; gap: 8px;`;
const Sub = styled.div`color: #94a3b8; margin-top: 4px; font-size: 13px;`;

const Button = styled.button`
  border: 1px solid rgba(56,189,248,.4); background: rgba(14,116,144,.22); color: #bae6fd;
  border-radius: 8px; padding: 8px 11px; font-size: 12px; font-weight: 700; cursor: pointer;
`;

const Kpis = styled.div`display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px;`;
const Kpi = styled.div<{ $accent?: string }>`
  background: rgba(15, 23, 42, 0.55); border: 1px solid rgba(148,163,184,.14);
  border-left: 3px solid ${p => p.$accent || '#38bdf8'}; border-radius: 10px; padding: 14px 16px;
  .label { color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  .value { font-size: 22px; font-weight: 700; color: #f8fafc; margin-top: 6px; }
`;

const SectionTitle = styled.h3`font-size: 14px; color: #cbd5e1; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: .05em;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; background: rgba(15, 23, 42, 0.45); border: 1px solid rgba(148,163,184,.12);
  th, td { text-align: left; padding: 11px 12px; border-bottom: 1px solid rgba(148,163,184,.1); font-size: 13px; }
  th { color: #94a3b8; font-size: 11px; text-transform: uppercase; }
  tbody tr.clickable { cursor: pointer; }
  tbody tr.clickable:hover { background: rgba(30, 41, 59, 0.65); }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

const Badge = styled.span<{ $tone?: string }>`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: ${p => p.$tone === 'private' ? 'rgba(139,92,246,.16)' : 'rgba(34,197,94,.16)'};
  color: ${p => p.$tone === 'private' ? '#c4b5fd' : '#86efac'};
`;

const Drawer = styled.div`
  position: fixed; top: 0; right: 0; height: 100vh; width: min(560px, 96vw); z-index: 1300;
  background: #0b1220; border-left: 1px solid rgba(148,163,184,.2); box-shadow: -16px 0 40px rgba(0,0,0,.45);
  overflow-y: auto; padding: 22px;
`;
const Backdrop = styled.div`position: fixed; inset: 0; background: rgba(2,6,23,.55); z-index: 1290;`;
const StatRow = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px; margin: 10px 0 4px;`;
const Stat = styled.div`
  .l { color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: .03em; }
  .v { color: #f1f5f9; font-size: 15px; font-weight: 600; margin-top: 2px; }
`;
const Input = styled.input`background: rgba(15,23,42,.8); border: 1px solid rgba(148,163,184,.25); border-radius: 7px; padding: 6px 8px; color: #e2e8f0; font-size: 13px; width: 90px;`;

const money = (v: any) => v == null ? '--' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(Number(v));
const pct = (v: any, dp = 2) => v == null ? '--' : `${Number(v).toFixed(dp)}%`;
const numfmt = (v: any, dp = 2) => v == null ? '--' : Number(v).toFixed(dp);

const MSRManagementScreen: React.FC<{ embedded?: boolean }> = () => {
  const navigate = useNavigate();
  const [msrs, setMsrs] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerPos, setDrawerPos] = useState<any | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [val, setVal] = useState<any | null>(null);
  const [mktRate, setMktRate] = useState('6.5');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [msrRes, posRes] = await Promise.all([
        axios.get('/api/credit/msr'),
        axios.get('/api/positions/latest'),
      ]);
      setMsrs(Array.isArray(msrRes.data) ? msrRes.data : []);
      const all = posRes.data?.content || posRes.data || [];
      setPositions((Array.isArray(all) ? all : []).filter((p: any) => p.assetClassName === 'RESIDENTIAL_MSR' && Number(p.quantity) !== 0));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load MSR data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runValuation = useCallback(async (positionId: number, rate?: string) => {
    try {
      const r = rate ?? mktRate;
      const res = await axios.get(`/api/credit/msr/positions/${positionId}/valuation`, {
        params: r ? { marketRatePct: r } : {},
      });
      setVal(res.data);
    } catch (err: any) {
      setVal({ error: err.response?.data?.error || err.message || 'Valuation failed' });
    }
  }, [mktRate]);

  const openDrawer = async (pos: any) => {
    setDrawerPos(pos); setDetail(null); setVal(null);
    try {
      const res = await axios.get(`/api/credit/msr/positions/${pos.id}/detail`);
      setDetail(res.data);
    } catch { /* detail optional */ }
    runValuation(pos.id);
  };

  const totalUpb = msrs.reduce((s, m) => s + Number(m.currentUpb || 0), 0);
  const totalMv = positions.reduce((s, p) => s + Number(p.marketValue || 0), 0);
  const wtdFee = totalUpb > 0 ? msrs.reduce((s, m) => s + Number(m.servicingFeeBps || 0) * Number(m.currentUpb || 0), 0) / totalUpb : 0;

  return (
    <Wrapper>
      <Header>
        <div>
          <Title>🧾 MSR — Mortgage Servicing Rights</Title>
          <Sub>Servicing-fee strips on prepaying residential pools — negative-duration IO economics. Value, multiple &amp; hedge metrics recompute on demand.</Sub>
        </div>
        <Button onClick={() => navigate('/trade-entry/msr')}>New MSR</Button>
      </Header>

      {error && <div style={{ color: '#fca5a5', marginBottom: 12 }}>{error}</div>}

      <Kpis>
        <Kpi $accent="#38bdf8"><div className="label">MSR Packages</div><div className="value">{msrs.length}</div></Kpi>
        <Kpi $accent="#a78bfa"><div className="label">Serviced UPB</div><div className="value">{money(totalUpb)}</div></Kpi>
        <Kpi $accent="#34d399"><div className="label">Market Value</div><div className="value">{money(totalMv)}</div></Kpi>
        <Kpi $accent="#fbbf24"><div className="label">Wtd Fee (bps)</div><div className="value">{numfmt(wtdFee, 1)}</div></Kpi>
        <Kpi $accent="#22d3ee"><div className="label">Positions</div><div className="value">{positions.length}</div></Kpi>
      </Kpis>

      {loading ? <div>Loading MSR…</div> : (
        <>
          <SectionTitle>Servicing Packages</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>MSR</th><th>Agency</th><th>Product</th><th className="num">UPB</th>
                <th className="num"># Loans</th><th className="num">WAC</th><th className="num">Fee bps</th>
                <th className="num">Multiple</th><th>CUSIP</th>
              </tr>
            </thead>
            <tbody>
              {msrs.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#64748b' }}>No MSR packages yet.</td></tr>}
              {msrs.map((m) => (
                <tr key={m.msrId}>
                  <td>{m.msrName}</td>
                  <td><Badge $tone={m.agency === 'PRIVATE' ? 'private' : 'agency'}>{m.agency}</Badge></td>
                  <td>{m.productType}</td>
                  <td className="num">{money(m.currentUpb)}</td>
                  <td className="num">{m.numLoans ?? '--'}</td>
                  <td className="num">{pct(m.wacPct)}</td>
                  <td className="num">{numfmt(m.servicingFeeBps, 1)}</td>
                  <td className="num">{m.acquisitionMultiple ? `${numfmt(m.acquisitionMultiple, 2)}×` : '--'}</td>
                  <td>{m.cusip}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <SectionTitle>Positions</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Security</th><th>CUSIP</th><th>Portfolio</th>
                <th className="num">UPB (face)</th><th className="num">Market Value</th><th className="num">Unrealized P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b' }}>No MSR positions.</td></tr>}
              {positions.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => openDrawer(p)}>
                  <td>{p.securityName}</td>
                  <td>{p.cusip}</td>
                  <td>{p.portfolioName}</td>
                  <td className="num">{money(p.quantity)}</td>
                  <td className="num">{money(p.marketValue)}</td>
                  <td className="num" style={{ color: Number(p.unrealizedPnl) < 0 ? '#f87171' : '#34d399' }}>{money(p.unrealizedPnl)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {drawerPos && (
        <>
          <Backdrop onClick={() => { setDrawerPos(null); setDetail(null); setVal(null); }} />
          <Drawer>
            <Header>
              <div>
                <Title style={{ fontSize: 18 }}>{drawerPos.securityName}</Title>
                <Sub>{drawerPos.cusip} · {drawerPos.portfolioName}</Sub>
              </div>
              <Button onClick={() => { setDrawerPos(null); setDetail(null); setVal(null); }}>Close</Button>
            </Header>

            {detail?.found && (
              <>
                <SectionTitle>Servicing Package</SectionTitle>
                <StatRow>
                  <Stat><div className="l">Agency / Product</div><div className="v">{detail.agency} · {detail.productType}</div></Stat>
                  <Stat><div className="l">Servicer</div><div className="v">{detail.servicerName || '--'}</div></Stat>
                  <Stat><div className="l">Current UPB</div><div className="v">{money(detail.currentUpb)}</div></Stat>
                  <Stat><div className="l"># Loans</div><div className="v">{detail.numLoans ?? '--'}</div></Stat>
                  <Stat><div className="l">WAC</div><div className="v">{pct(detail.wacPct)}</div></Stat>
                  <Stat><div className="l">Servicing Fee</div><div className="v">{numfmt(detail.servicingFeeBps, 1)} bps</div></Stat>
                  <Stat><div className="l">Cost / Ancillary $/loan</div><div className="v">{numfmt(detail.costToServicePerLoan, 0)} / {numfmt(detail.ancillaryIncomePerLoan, 0)}</div></Stat>
                  <Stat><div className="l">Acq. Multiple</div><div className="v">{detail.acquisitionMultiple ? `${numfmt(detail.acquisitionMultiple, 2)}×` : '--'}</div></Stat>
                </StatRow>
              </>
            )}

            <SectionTitle>Valuation (transparent — OAS via Intex)</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>Refi benchmark rate (%)</span>
              <Input type="number" step="0.25" value={mktRate}
                onChange={(e) => setMktRate(e.target.value)} />
              <Button onClick={() => runValuation(drawerPos.id)}>Reprice</Button>
            </div>

            {!val ? <div style={{ color: '#64748b' }}>Computing…</div> : val.error ? (
              <div style={{ color: '#fca5a5' }}>{val.error}</div>
            ) : (
              <>
                <StatRow>
                  <Stat><div className="l">MSR Value</div><div className="v">{money(val.msrValue)}</div></Stat>
                  <Stat><div className="l">Value % of UPB</div><div className="v">{pct(val.valuePerUpbPct, 3)}</div></Stat>
                  <Stat><div className="l">MSR Multiple</div><div className="v">{numfmt(val.msrMultiple, 2)}×</div></Stat>
                  <Stat><div className="l">Net Servicing CF (Yr 1)</div><div className="v">{money(val.netServicingCfYr1)}</div></Stat>
                  <Stat><div className="l">WAL</div><div className="v">{numfmt(val.weightedAvgLifeYears, 2)} yrs</div></Stat>
                  <Stat><div className="l">Assumed CPR</div><div className="v">{pct(val.assumedCprPct)}</div></Stat>
                  <Stat>
                    <div className="l">Effective Duration</div>
                    <div className="v" style={{ color: Number(val.effectiveDuration) < 0 ? '#34d399' : '#f1f5f9' }}>
                      {numfmt(val.effectiveDuration, 2)}
                    </div>
                  </Stat>
                  <Stat><div className="l">Convexity</div><div className="v">{numfmt(val.convexity, 1)}</div></Stat>
                </StatRow>
                <div style={{ color: Number(val.effectiveDuration) < 0 ? '#86efac' : '#94a3b8', fontSize: 12, margin: '6px 0 4px' }}>
                  {val.durationSign}
                </div>

                <SectionTitle>Rate-Shock Sensitivity</SectionTitle>
                <Table>
                  <thead><tr><th className="num">Shock</th><th className="num">CPR</th><th className="num">MSR Value</th><th className="num">Multiple</th><th className="num">Δ Value</th></tr></thead>
                  <tbody>
                    {(val.rateShockGrid || []).map((r: any, i: number) => (
                      <tr key={i} style={{ background: r.rateShockBps === 0 ? 'rgba(56,189,248,.08)' : undefined }}>
                        <td className="num">{r.rateShockBps > 0 ? '+' : ''}{r.rateShockBps} bps</td>
                        <td className="num">{pct(r.cprPct)}</td>
                        <td className="num">{money(r.msrValue)}</td>
                        <td className="num">{numfmt(r.multiple, 2)}×</td>
                        <td className="num" style={{ color: Number(r.valueChangePct) < 0 ? '#f87171' : '#34d399' }}>{pct(r.valueChangePct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {val.oasNote && <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>{val.oasNote}</div>}
              </>
            )}
          </Drawer>
        </>
      )}
    </Wrapper>
  );
};

export default MSRManagementScreen;
